import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LogoSlideshow } from './LogoSlideshow';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Moon,
  Heart,
  Users,
  Smartphone,
  GitBranch,
  Clock,
  User,
  Settings
} from 'lucide-react';

interface MenuItem {
  icon: LucideIcon;
  label: string;
  path: string;
  roles?: ('administrator' | 'owner' | 'operator')[];
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    title: "Dashboard",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", path: "/" }
    ]
  },
  {
    title: "Health Tracking",
    items: [
      { icon: Moon, label: "Sleep Monitoring", path: "/sleep-monitoring" },
      { icon: Heart, label: "Vital Signs", path: "/vital-signs" }
    ]
  },
  {
    title: "Company Management",
    items: [
      { icon: Users, label: "Employee Management", path: "/employees", roles: ['administrator', 'owner', 'operator'] },
      { icon: Smartphone, label: "Device Management", path: "/devices" },
      { icon: GitBranch, label: "Group Management", path: "/group-management", roles: ['administrator', 'owner', 'operator'] },
      { icon: Clock, label: "Shift Management", path: "/shift-settings" }
    ]
  },
  {
    title: "Settings",
    items: [
      { icon: User, label: "User Management", path: "/user-management", roles: ['administrator', 'owner', 'operator'] },
      { icon: Settings, label: "Parameter Settings", path: "/parameter-settings", roles: ['administrator', 'owner'] }
    ]
  }
];

/**
 * Helper function to determine if a menu item is active
 * @param itemPath - The path of the menu item
 * @param currentPath - The current pathname
 * @returns true if the menu item should be highlighted as active
 */
const isActiveMenuItem = (itemPath: string, currentPath: string): boolean => {
  // For homepage, only match if path is exactly the same
  if (itemPath === '/') {
    return currentPath === '/';
  }
  // For other paths, match if currentPath starts with itemPath
  return currentPath === itemPath || currentPath.startsWith(itemPath);
};

/**
 * Helper function to check if user has access to menu item
 * @param item - The menu item
 * @param userRole - The user's role
 * @returns true if user can see this menu item
 */
const canAccessMenuItem = (item: MenuItem, userRole?: string): boolean => {
  // If no roles specified, everyone can access
  if (!item.roles) {
    return true;
  }
  // If roles specified, check if user's role is in the list
  return userRole ? item.roles.includes(userRole as any) : false;
};

/**
 * Sidebar component for desktop navigation
 * Displays navigation menu with active route highlighting and role-based filtering
 * Hidden on mobile devices (shown via BottomNav instead)
 */
export const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  // Filter menu sections based on user role
  const visibleMenuSections = menuSections.map(section => ({
    ...section,
    items: section.items.filter(item => canAccessMenuItem(item, user?.role))
  })).filter(section => section.items.length > 0);

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="w-64 bg-white fixed left-0 top-0 bottom-0 border-r border-gray-200 z-20 md:flex hidden flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-center h-24 flex-shrink-0">
          <LogoSlideshow size="lg" alt="Indosat" />
        </div>

        <nav className="p-4 flex-1 overflow-y-auto min-h-0">
          {visibleMenuSections.map((section, sectionIndex) => (
            <div key={section.title} className={sectionIndex > 0 ? "mt-6" : ""}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
                {section.title}
              </h3>
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.path}>
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className={`flex items-center p-3 rounded-lg transition-colors w-full text-left ${
                        isActiveMenuItem(item.path, location.pathname)
                          ? "bg-blue-50 text-blue-600 border-r-2 border-blue-600"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      <span className="mr-3">
                        <item.icon size={20} strokeWidth={1.5} />
                      </span>
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Mobile Menu Toggle Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="hidden fixed top-4 left-4 z-30 p-2 bg-white rounded-lg shadow-md"
        aria-label="Toggle mobile menu"
      >
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {isMobileMenuOpen ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          )}
        </svg>
      </button>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-20"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="md:hidden w-64 bg-white fixed left-0 top-0 bottom-0 border-r border-gray-200 z-30 transform transition-transform flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-center h-24 flex-shrink-0">
              <LogoSlideshow size="lg" alt="Indosat" />
            </div>

            <nav className="p-4 flex-1 overflow-y-auto min-h-0">
              {visibleMenuSections.map((section, sectionIndex) => (
                <div key={section.title} className={sectionIndex > 0 ? "mt-6" : ""}>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
                    {section.title}
                  </h3>
                  <ul className="space-y-1">
                    {section.items.map((item) => (
                      <li key={item.path}>
                        <button
                          onClick={() => handleNavigation(item.path)}
                          className={`flex items-center p-3 rounded-lg transition-colors w-full text-left ${
                            isActiveMenuItem(item.path, location.pathname)
                              ? "bg-blue-50 text-blue-600 border-r-2 border-blue-600"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          }`}
                        >
                          <span className="mr-3">
                            <item.icon size={20} strokeWidth={1.5} />
                          </span>
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>
        </>
      )}
    </>
  );
};
